import { useState, useEffect, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  Card, Button, Modal, Select, Input, Textarea, EmptyState, Spinner, PageHeader,
  StatCard, IconButton, CardSkeleton, Chip, StickyActions, cx,
} from "../components/UI";
import { DrawingViewerModal, getFileIcon, getFileBadgeStyle, formatBytes } from "../components/DrawingViewer";
import DrawingUpload from "../components/DrawingUpload";
import { deleteDrawingFile } from "../utils/firebase";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import toast from "react-hot-toast";
import { SiteSearchPopup } from "../components/SiteSearchPopup";
import {
  Upload, Eye, Download, History, Pencil, Trash2, Ruler, MapPin, Plus, FileText,
} from "lucide-react";

export default function AdminDrawings() {
  const { user } = useAuth();

  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ siteId: "", fileType: "", search: "" });

  const [uploadModal, setUploadModal] = useState(false);
  const [uploadSiteId, setUploadSiteId] = useState("");
  const [viewDrawing, setViewDrawing] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", tags: "" });
  const [saving, setSaving] = useState(false);

  const fetchDrawings = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.siteId) p.set("siteId", filters.siteId);
      if (filters.fileType) p.set("fileType", filters.fileType);
      if (filters.search) p.set("search", filters.search);
      const { data } = await api.get(`/drawings/admin?${p}`);
      setDrawings(data.data || []);
    } catch { toast.error("Failed to load drawings"); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchDrawings(); }, [fetchDrawings]);

  const openHistory = async (drawing) => {
    const siteId = drawing.siteId?._id || drawing.siteId;
    setHistoryModal({ siteId, drawingCode: drawing.drawingCode, title: drawing.title });
    setHistLoading(true);
    try {
      const { data } = await api.get(`/drawings/site/${siteId}/history/${drawing.drawingCode}`);
      setHistory(data.data || []);
    } catch { toast.error("Failed to load history"); }
    finally { setHistLoading(false); }
  };

  const openEdit = (d) => {
    setEditModal(d);
    setEditForm({ title: d.title, description: d.description || "", tags: (d.tags || []).join(", ") });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/drawings/${editModal._id}`, editForm);
      toast.success("Drawing updated!");
      setEditModal(null);
      fetchDrawings();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (drawing) => {
    if (!window.confirm(`Delete ${drawing.title} v${drawing.version}?\nThis permanently removes the file.`)) return;
    try {
      const { data } = await api.delete(`/drawings/${drawing._id}`);
      if (data.data?.firebasePath) await deleteDrawingFile(data.data.firebasePath);
      toast.success("Drawing deleted");
      fetchDrawings();
      if (historyModal) {
        const { data: h } = await api.get(`/drawings/site/${historyModal.siteId}/history/${historyModal.drawingCode}`);
        setHistory(h.data || []);
      }
    } catch { toast.error("Delete failed"); }
  };

  const handleDeleteAll = async (drawing) => {
    if (!window.confirm(`Delete ALL versions of "${drawing.title}"? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/drawings/site/${drawing.siteId?._id || drawing.siteId}/all/${drawing.drawingCode}`);
      if (data.data?.firebasePaths) await Promise.all(data.data.firebasePaths.map(deleteDrawingFile));
      toast.success("All versions deleted");
      setHistoryModal(null);
      fetchDrawings();
    } catch { toast.error("Delete failed"); }
  };

  // Group by site so the page reads as "site → its drawings"
  const grouped = drawings.reduce((acc, d) => {
    const siteKey = d.siteId?._id || d.siteId;
    const siteName = d.siteId?.name || "Unknown Site";
    if (!acc[siteKey]) acc[siteKey] = { name: siteName, items: [] };
    acc[siteKey].items.push(d);
    return acc;
  }, {});

  const ff = (k) => ({ value: filters[k], onChange: (e) => setFilters({ ...filters, [k]: e.target.value }) });

  const downloadCls = "inline-grid place-items-center w-8 h-8 rounded-lg border border-ink-300 bg-white text-ink-600 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50 transition-all shrink-0";

  return (
    <AdminLayout>
      <div className="space-y-5">
        <PageHeader
          title="Drawings"
          subtitle="CAD files and engineering drawings across all sites"
          action={
            <Button onClick={() => { setUploadSiteId(""); setUploadModal(true); }} icon={<Upload size={16} />} className="w-full sm:w-auto">
              Upload Drawing
            </Button>
          }
        />

        {/* ══ Filters ══ */}
        <Card className="p-3 sm:p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SiteSearchPopup value={filters.siteId} onChange={(v) => setFilters({ ...filters, siteId: v })}
              label="Filter by Site" placeholder="All Sites" />
            <Select label="File Type" {...ff("fileType")}>
              <option value="">All Types</option>
              <option value="pdf">PDF</option>
              <option value="dwg">DWG</option>
              <option value="dxf">DXF</option>
            </Select>
            <Input label="Search" placeholder="Search by title…" {...ff("search")} />
          </div>
        </Card>

        {/* ══ Counts by type ══ */}
        {drawings.length > 0 && (
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            {[
              { type: "pdf", label: "PDF files", color: "red" },
              { type: "dwg", label: "DWG files", color: "blue" },
              { type: "dxf", label: "DXF files", color: "cyan" },
            ].map(({ type, label, color }) => (
              <StatCard key={type} icon={<FileText size={17} />} color={color} label={label}
                value={drawings.filter(d => d.fileType === type).length} />
            ))}
          </div>
        )}

        {/* ══ Grouped list ══ */}
        {loading ? (
          <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} lines={2} />)}</div>
        ) : drawings.length === 0 ? (
          <Card>
            <EmptyState icon={<Ruler size={22} />} title="No drawings yet"
              description="Upload DWG, DXF or PDF files and the crew will see them instantly."
              action={<Button onClick={() => setUploadModal(true)} icon={<Upload size={16} />}>Upload Drawing</Button>} />
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([siteId, group]) => (
              <section key={siteId}>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <MapPin size={15} className="text-ink-400 shrink-0" />
                  <h3 className="text-ink-900 font-semibold truncate">{group.name}</h3>
                  <span className="text-[11px] text-ink-500 bg-ink-100 px-2 py-0.5 rounded-full shrink-0">
                    {group.items.length} drawing{group.items.length !== 1 ? "s" : ""}
                  </span>
                  <Button size="xs" variant="ghost" icon={<Plus size={13} />}
                    onClick={() => { setUploadSiteId(siteId); setUploadModal(true); }} className="ml-auto shrink-0">
                    Upload
                  </Button>
                </div>

                <div className="grid gap-3">
                  {group.items.map((d) => (
                    <Card key={d._id} className="p-4" hover>
                      {/* actions lead the row so they're the first thing in reach */}
                      <div className="flex items-center gap-1.5 flex-wrap pb-3 mb-3 border-b border-ink-100 sm:hidden">
                        <Button size="xs" variant="outline" icon={<Eye size={13} />} onClick={() => setViewDrawing(d)}>Preview</Button>
                        <a href={d.firebaseURL} download={d.fileName} target="_blank" rel="noopener noreferrer"
                          title="Download" aria-label="Download" className={downloadCls}>
                          <Download size={15} />
                        </a>
                        <IconButton size="sm" variant="outline" icon={<History size={15} />} onClick={() => openHistory(d)} title="Version history" />
                        <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} onClick={() => openEdit(d)} title="Edit details" />
                        <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(d)} title="Delete" className="ml-auto" />
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                        <div className="hidden sm:flex items-center gap-1.5 shrink-0 order-first">
                          <Button size="xs" variant="outline" icon={<Eye size={13} />} onClick={() => setViewDrawing(d)}>Preview</Button>
                          <a href={d.firebaseURL} download={d.fileName} target="_blank" rel="noopener noreferrer"
                            title="Download" aria-label="Download" className={downloadCls}>
                            <Download size={15} />
                          </a>
                          <IconButton size="sm" variant="outline" icon={<History size={15} />} onClick={() => openHistory(d)} title="Version history" />
                          <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} onClick={() => openEdit(d)} title="Edit details" />
                          <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(d)} title="Delete" />
                        </div>

                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <span className={cx("w-11 h-11 rounded-xl border grid place-items-center text-xl shrink-0", getFileBadgeStyle(d.fileType))}>
                            {getFileIcon(d.fileType)}
                          </span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-ink-900 font-semibold truncate">{d.title}</span>
                              <span className={cx("text-[10px] px-1.5 py-0.5 rounded border uppercase font-mono", getFileBadgeStyle(d.fileType))}>{d.fileType}</span>
                              <span className="text-[10px] bg-ink-100 text-ink-700 border border-ink-200 px-1.5 py-0.5 rounded-full font-mono">v{d.version}</span>
                              <span className="text-[10px] text-ink-400 font-mono bg-ink-50 px-2 py-0.5 rounded">{d.drawingCode}</span>
                            </div>
                            <div className="flex gap-x-3 gap-y-1 mt-1.5 text-xs text-ink-400 flex-wrap">
                              <span className="tnum">{formatBytes(d.fileSize)}</span>
                              <span>by {d.uploadedByName || d.uploadedByRole}</span>
                              <span>{new Date(d.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                            </div>
                            {d.tags?.length > 0 && (
                              <div className="flex gap-1.5 mt-2 flex-wrap">
                                {d.tags.map(t => <Chip key={t} tone="brand">#{t}</Chip>)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* ══ Upload ══ */}
      <Modal open={uploadModal} onClose={() => setUploadModal(false)} title="Upload Drawing"
        subtitle="DWG, DXF or PDF" icon={<Upload size={18} />} size="lg">
        <div className="mb-4">
          <SiteSearchPopup value={uploadSiteId} onChange={(v) => setUploadSiteId(v)} label="Select Site" placeholder="— Select a site —" />
        </div>
        {uploadSiteId ? (
          <DrawingUpload
            siteId={uploadSiteId}
            adminId={user?._id || user?.id}
            existingCodes={drawings.filter(d => (d.siteId?._id || d.siteId) === uploadSiteId).map(d => d.drawingCode)}
            onSuccess={() => { setUploadModal(false); fetchDrawings(); }}
            onCancel={() => setUploadModal(false)}
          />
        ) : (
          <div className="border-2 border-dashed border-ink-200 rounded-2xl py-10 text-center">
            <p className="text-ink-400 text-sm">Pick a site above to continue</p>
          </div>
        )}
      </Modal>

      {/* ══ Version history ══ */}
      <Modal open={!!historyModal} onClose={() => setHistoryModal(null)}
        title="Version History" subtitle={historyModal?.title || historyModal?.drawingCode}
        icon={<History size={18} />} size="lg">
        {histLoading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : (
          <div className="space-y-3">
            {history.map((d) => (
              <div key={d._id} className={cx("p-3.5 rounded-2xl border",
                d.isLatest ? "border-emerald-200 bg-emerald-50/50" : "border-ink-200 bg-ink-50/50")}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={cx("w-9 h-9 rounded-xl grid place-items-center text-xs font-bold font-mono shrink-0",
                      d.isLatest ? "bg-emerald-600 text-white" : "bg-ink-200 text-ink-700")}>
                      v{d.version}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-ink-800 font-medium text-sm truncate">{d.title}</span>
                        {d.isLatest && (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">Latest</span>
                        )}
                      </div>
                      <div className="text-ink-400 text-xs mt-0.5">
                        {formatBytes(d.fileSize)} · {d.uploadedByName || d.uploadedByRole} ·{" "}
                        {new Date(d.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <IconButton size="sm" variant="outline" icon={<Eye size={15} />} onClick={() => setViewDrawing(d)} title="Preview" />
                    <a href={d.firebaseURL} target="_blank" rel="noopener noreferrer" download={d.fileName}
                      title="Download" aria-label="Download" className={downloadCls}>
                      <Download size={15} />
                    </a>
                    <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(d)} title="Delete this version" />
                  </div>
                </div>
              </div>
            ))}

            {history.length > 1 && (
              <div className="pt-2 border-t border-ink-100">
                <Button variant="dangerSoft" size="sm" icon={<Trash2 size={14} />} onClick={() => handleDeleteAll(history[0])}>
                  Delete all versions
                </Button>
              </div>
            )}

            <div className="pt-4 border-t border-ink-100">
              <p className="text-sm font-semibold text-ink-700 mb-3">Upload a new version</p>
              <DrawingUpload
                siteId={historyModal?.siteId}
                adminId={user?._id || user?.id}
                existingCodes={[historyModal?.drawingCode].filter(Boolean)}
                onSuccess={() => {
                  fetchDrawings();
                  openHistory({ siteId: { _id: historyModal.siteId }, drawingCode: historyModal.drawingCode, title: historyModal.title });
                }}
                onCancel={() => setHistoryModal(null)}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* ══ Edit details ══ */}
      <Modal open={!!editModal} onClose={() => setEditModal(null)} title="Edit Drawing" subtitle={editModal?.drawingCode} icon={<Pencil size={18} />}>
        <form onSubmit={handleEditSave} className="space-y-4">
          <Input label="Title" required value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
          <Textarea label="Description" rows={2} value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="What does this drawing cover?" />
          <Input label="Tags" hint="Comma separated" value={editForm.tags}
            onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} placeholder="structural, approved, level-1" />

          <StickyActions>
            <Button type="button" variant="secondary" onClick={() => setEditModal(null)} block className="sm:w-auto">Cancel</Button>
            <Button type="submit" loading={saving} block className="sm:w-auto">Save Changes</Button>
          </StickyActions>
        </form>
      </Modal>

      <DrawingViewerModal drawing={viewDrawing} onClose={() => setViewDrawing(null)} />
    </AdminLayout>
  );
}
